import { MapPinned, Monitor, Pill, CalendarDays } from 'lucide-react';
import { benTaubOr, BB_DOC } from './benTaubOr';

/*
 * Ben Taub Remote (out-of-OR) rotation. Built from the Blackboard Remote Orientation
 * guide. See docs/blackboard-ben-taub-subspecialties.md.
 * Contacts, Locations and Documents extend the General OR rotation.
 */

const REMOTE = BB_DOC('_257826_1');

export const benTaubRemote = {
  accent: benTaubOr.accent,
  icon: MapPinned,
  tagline: 'GI, IR, CT, MRI, bronchoscopy, TEE, nuclear medicine',
  notesKey: 'notes:ben-taub:remote',

  toolSections: [
    { title: 'Clinical tools', tools: [
      { id: 'sites', title: 'Sites & Setups', subtitle: 'What each suite has and what it needs', icon: Monitor, action: 'guide:sites', featured: true },
      { id: 'gi', title: 'GI Sedation', subtitle: 'Propofol, positioning, laryngospasm', icon: Pill, action: 'guide:gi' },
    ] },
    { title: 'Logistics', tools: [
      { id: 'day', title: 'Your Day', subtitle: 'Assignments, start times, recovery', icon: CalendarDays, action: 'guide:day' },
    ] },
  ],

  locations: {
    ...benTaubOr.locations,
    groups: [
      { id: 'remote', title: 'Procedure suites', items: [
        { name: 'IR', floor: '1st floor', where: 'Next to the Emergency Center', note: 'Two suites, neuro and body. Set up in the right one. 07:15 starts are tracked' },
        { name: 'CT and MRI', floor: '1st floor', where: 'Next to the Emergency Center', note: 'MRI: badge, phone, jewelry and anything metal stay outside' },
        { name: 'GI', floor: '6th floor', where: 'Opposite the STAT elevator', note: 'Own Pyxis. Three rooms' },
        { name: 'Bronchoscopy suite', floor: '6th floor', where: 'Diagonally across from GI' },
        { name: 'TEE', floor: '3rd floor', where: 'OG8', note: 'No gas scavenging: TIVA only' },
        { name: 'Nuclear medicine', floor: '6C', note: 'No anesthesia machine. Portable monitor and pumps' },
      ] },
      ...benTaubOr.locations.groups,
    ],
  },

  documents: {
    storageKey: 'ben-taub-remote-pinned-docs',
    groups: [
      { id: 'remote', title: 'Remote', items: [
        { title: 'Remote orientation', subtitle: 'Blackboard', url: REMOTE },
      ] },
      ...benTaubOr.documents.groups,
    ],
  },

  guides: {
    sites: {
      title: 'Sites & Setups',
      sections: [
        { title: 'Everywhere', blocks: [{ type: 'bullets', items: [
          'Bring every emergency drug and fluid you could need, and extra paralytic. The OR pharmacy is far away and help is slow',
          'Drugs, fluids and pressor infusions from the 4th floor OR pharmacy before you go. GI has its own Pyxis',
          'Talk to the remote anesthesia tech before setup and after the case so the equipment comes back',
          'Each suite has a small preop area. ICU patients come straight from the ICU',
          'Non-ICU patients recover in the PACU wherever the procedure was',
        ] }] },
        { title: 'IR', blocks: [{ type: 'bullets', items: [
          'Full setup: machine, Blue Bell cart',
          'Extensions on the IV, arterial line and circuit. Keep lines clear of the C-arm',
          'Two suites, neuro and body. Set up in the right one',
          'Mostly general, a fair number of MAC',
          { text: '07:15 start: in the room at 07:15, no exceptions. Consent and IV in the room if you have to', warn: true },
        ] }] },
        { title: 'CT', blocks: [{ type: 'bullets', items: [
          'Same as IR, less space, shorter procedures',
        ] }] },
        { title: 'MRI', blocks: [{ type: 'bullets', items: [
          'MRI-specific machine and monitors',
          'Induce outside the scanner on a transport monitor and hand-chart those vitals, then wheel in and switch to the MRI monitors',
          'Badge, phone, jewelry, anything metal: off before you enter',
          'General or MAC',
        ] }] },
        { title: 'Bronchoscopy', blocks: [{ type: 'bullets', items: [
          'Full setup. Bronchoscope through an LMA, TIVA, a small dose of paralytic against coughing',
          'Ask the attending how they run the TIVA',
        ] }] },
        { title: 'TEE', blocks: [{ type: 'bullets', items: [
          'Full setup but TIVA only. OG8 has no scavenging',
        ] }] },
        { title: 'Nuclear medicine', blocks: [{ type: 'bullets', items: [
          'No machine. Portable monitor and pumps for TIVA or MAC',
          'Timed: tracer injected, scanner exactly 1 h later, scan about 25 min',
        ] }] },
        { title: 'GI', blocks: [{ type: 'bullets', items: [
          'Full setup. Mostly TIVA with a POM mask. Some ERCP and EUS are intubated',
          'See GI Sedation',
        ] }] },
      ],
      sources: [{ title: 'Remote orientation guide', href: REMOTE, detail: 'Blackboard, sign-in required' }],
    },

    gi: {
      title: 'GI Sedation',
      sections: [
        { title: 'Propofol', blocks: [{ type: 'rows', rows: [
          { label: 'Induction', value: '0.5–1 mg/kg in divided doses', note: 'Start small; apnea is the enemy' },
          { label: 'Infusion', value: '100–200 mcg/kg/min' },
        ] }, { type: 'bullets', items: [
          'Titrate to comfort, EtCO2 and respiratory rate',
          'Every GI case is a general anesthetic: consent for general, chart TIVA',
        ] }] },
        { title: 'Position', blocks: [{ type: 'rows', rows: [
          { label: 'EGD', value: 'Supine' },
          { label: 'Colonoscopy', value: 'Lateral' },
          { label: 'ERCP', value: 'Prone' },
        ] }] },
        { title: 'Laryngospasm', blocks: [{ type: 'bullets', items: [
          { text: 'Airway rescue equipment and drugs within reach for every case', warn: true },
        ] }] },
      ],
      sources: [{ title: 'Remote orientation guide', href: REMOTE, detail: 'Blackboard, sign-in required' }],
    },

    day: {
      title: 'Your Day',
      sections: [
        { title: 'The evening before', blocks: [{ type: 'bullets', items: [
          'Ask the attending assigned to remote whether there are remote cases. None means GI',
        ] }] },
        { title: 'Morning', blocks: [{ type: 'bullets', items: [
          'Arrive 06:30 whatever the scheduled time. Starts move and add-ons appear. Check in with the CON',
          'GI: agree with the CRNAs who takes the first patient. Cases start 07:30 except Thursdays',
          'Remote: drugs and fluids from the OR pharmacy, then to the suite. Meet the patient in its preop area',
          'Tuesday: senior seminar first, no setup, then report to the CON',
        ] }] },
        { title: 'GI day', blocks: [{ type: 'bullets', items: [
          'Three rooms, four providers: rotate until the list is done. An empty room is lost time',
          'Inpatients need consent and preop; outpatients have been through PACT',
          'Most lists finish 14:00–16:00. Dismissed after, unless assigned late',
        ] }] },
        { title: 'Remote day', blocks: [{ type: 'bullets', items: [
          'Start times and durations vary. Later case: keep checking in',
          'Done early: tell the CON',
        ] }] },
      ],
      sources: [{ title: 'Remote orientation guide', href: REMOTE, detail: 'Blackboard, sign-in required' }],
    },
  },
};
